import { useState, useEffect, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next"
import EntityList from "../../components/EntityList";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Alert,
} from "@mui/material";
import api from "../../services/api";
import { Horse } from "@/src/interfaces";
import HorseLoader from "../../components/loadingHorse";
import { useCryptoNavigation } from "../../App";

// Interface para dados de raças
interface BreedData {
  horseBreedId: number;
  horseBreedName: string;
  quantidadeCavalos?: number;
  [key: string]: any;
}

export default function HorseBreedList() {
  const { t } = useTranslation()
  console.log("RENDERING HorseBreedList");
  console.log("⚠️ IMPORTANTE: Este componente usa encryptedPageName='racas' para navegação");
  
  const { navigateToPage } = useCryptoNavigation();
  
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedBreed, setSelectedBreed] = useState<BreedData | null>(null);
  const [loadingHorses, setLoadingHorses] = useState(false);
  const [horses, setHorses] = useState<Horse[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [allHorses, setAllHorses] = useState<Horse[]>([]);
  const [horsesLoaded, setHorsesLoaded] = useState(false);
  const [breedCounts, setBreedCounts] = useState<{[key: number]: number}>({});
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  // IMPORTANTE: O nome da página criptografada deve ser 'racas' 
  // para corresponder ao usado no HorseBreedForm
  // Função para navegar para novo
  const handleNew = () => {
    console.log("🚀 Navegando para nova raça");
    navigateToPage('racas', { mode: 'new' });
  };

  // Função para navegar para edição
  const handleEdit = (breedId: number | string) => {
    console.log("✏️ Navegando para editar raça ID:", breedId);
    navigateToPage('racas', { mode: 'edit', id: String(breedId) });
  };

  // useEffect otimizado para carregar cavalos APENAS UMA VEZ
  useEffect(() => {
    let mounted = true;
    
    const fetchAllHorses = async () => {
      // Evitar múltiplas chamadas
      if (horsesLoaded) {
        
        return;
      }
      
      try {
        const response = await api.get("/horse/1");
        console.log("Resposta bruta da API:", response);
        
        // Verificar se componente ainda está montado
        if (!mounted) return;
        
        if (response.data && response.data.horses && Array.isArray(response.data.horses)) {
          const horses = response.data.horses as Horse[];
          setAllHorses(horses);
          setHorsesLoaded(true);
          
          // Calcular as contagens por raça
          const counts: {[key: number]: number} = {};
          horses.forEach((horse: Horse) => {
            if (horse.horseBreed !== undefined) {
              const breedId = Number(horse.horseBreed);
              counts[breedId] = (counts[breedId] || 0) + 1;
            }
          });
          
          console.log("Contagens calculadas:", counts);
          setBreedCounts(counts);
          
          
          // Forçar re-render após carregar os dados com pequeno delay
          setTimeout(() => {
            setRefreshTrigger(prev => prev + 1);
          }, 100);
        } else {
          console.error("Formato inesperado de resposta da API:", response.data);
        }
      } catch (error) {
        console.error("Erro ao carregar cavalos:", error);
      }
    };
    
    fetchAllHorses();
    
    // Cleanup
    return () => {
      mounted = false;
    };
  }, []); // Array vazio - executar APENAS no mount

  // Memoizar transformBreedData para evitar recriação a cada render
  const transformBreedData = useCallback((breeds: any[]): any[] => {
    console.log("transformBreedData sendo chamada com", breeds.length, "raças");
    console.log("Estado atual: horsesLoaded =", horsesLoaded, "allHorses.length =", allHorses.length);
    console.log("breedCounts atual:", breedCounts);
    
    return breeds.map((breed: BreedData) => {
      const breedId = breed.horseBreedId;
      // Se cavalos ainda não foram carregados, mostrar 0, senão usar a contagem real
      const count = horsesLoaded ? (breedCounts[breedId] || 0) : 0;
      console.log(`Raça ${breed.horseBreedName} (ID: ${breedId}) tem ${count} cavalos`);
      
      return {
        ...breed,
        quantidadeCavalos: count
      };
    });
  }, [breedCounts, horsesLoaded]);

  // Memoizar handleShowHorses
  const handleShowHorses = useCallback((breed: BreedData) => {
    console.log("handleShowHorses chamado para raça:", breed);
    
    if (!breed || !breed.horseBreedId) {
      console.error("Dados da raça inválidos:", breed);
      setError(t("horseBreeds.invalidData"));
      return;
    }
    
    const breedId = breed.horseBreedId;
    console.log(`Abrindo modal para raça ID ${breedId} (${breed.horseBreedName})`);
    
    setLoadingHorses(true);
    setError(null);
    setSelectedBreed(breed);
    setModalOpen(true);
    
    try {
      if (horsesLoaded && allHorses.length > 0) {
        console.log(`Filtrando cavalos para raça ${breedId} de ${allHorses.length} cavalos`);
        
        const horsesOfBreed = allHorses.filter((horse: Horse) => {
          const horseBreedId = horse.horseBreed;
          const matches = horseBreedId === breedId || String(horseBreedId) === String(breedId);
          if (matches) {
            console.log(`Cavalo ${horse.horseName} (ID: ${horse.horseId}) pertence à raça ${breedId}`);
          }
          return matches;
        });
        
        console.log(`Encontrados ${horsesOfBreed.length} cavalos para raça ${breedId}`);
        setHorses(horsesOfBreed);
        
        if (horsesOfBreed.length === 0) {
          setError(`Não foram encontrados cavalos para a raça ${breed.horseBreedName}.`);
        }
      } else {
        console.log("Cavalos não foram carregados ainda");
        setError(t("horseBreeds.horsesNotLoaded"));
      }
    } catch (err) {
      console.error("Erro ao processar cavalos:", err);
      setError("Ocorreu um erro ao processar os cavalos.");
    } finally {
      setLoadingHorses(false);
    }
  }, [horsesLoaded, allHorses]);

  // Memoizar handleViewDetails
  const handleViewDetails = useCallback((breed: BreedData) => {
    console.log("Visualizando detalhes da raça:", breed);
    handleShowHorses(breed);
  }, [handleShowHorses]);

  // Memoizar handleCloseModal
  const handleCloseModal = useCallback(() => {
    setModalOpen(false);
    setSelectedBreed(null);
    setHorses([]);
    setError(null);
  }, []);

  // Memorizar formatDate
  const formatDate = (dateString: string | null): string => {
    if (!dateString) return '-';
    
    // Assumindo que a data vem no formato DD/MM/YYYY
    const parts = dateString.split('/');
    if (parts.length !== 3) return t('common.invalidDate');
    
    const [day, month, year] = parts;
    
    // Validação básica
    if (parseInt(day) < 1 || parseInt(day) > 31 || 
        parseInt(month) < 1 || parseInt(month) > 12) {
      return t('common.invalidDate');
    }
    
    return `${day}/${month}/${year}`;
  };

  // Memoizar columns para evitar recriação
  const columns = useMemo(() => [
    {
      id: "horseBreedName",
      label: t("fields.name"),
      minWidth: 170
    },
    {
      id: "quantidadeCavalos",
      label: t("horseBreeds.horsesCount"),
      minWidth: 120,
      align: "center" as const,
      format: (value: number, row: BreedData) => {
        return (
          <Typography
            component="span"
            sx={{
              cursor: 'pointer',
              color: 'primary.main',
              '&:hover': {
                textDecoration: 'underline'
              }
            }}
            onClick={(e) => {
              e.stopPropagation();
              console.log("Clique na quantidade para raça:", row);
              handleShowHorses(row);
            }}
          >
            {value}
          </Typography>
        );
      }
    }
  ], [handleShowHorses]);

  // Debug: Log das contagens atuais
  console.log("Estado atual das contagens:", breedCounts);
  console.log("Cavalos carregados:", horsesLoaded);
  console.log("Total de cavalos:", allHorses.length);
  console.log("RefreshTrigger:", refreshTrigger);

  return (
    <>
      
      <EntityList
        title={t("horseBreeds.title")}
        endpoint="/horse_breed"
        columns={columns}
        addPath="" // Não usado com navegação criptografada
        editPathPrefix="" // Não usado com navegação criptografada
        idField="horseBreedId"
        transformData={transformBreedData}
        dataPath="breed"
        onRowClick={handleViewDetails}
        useEncryptedNavigation={true}
        encryptedPageName="racas"
        onAddClick={handleNew}
        onEditClick={handleEdit}
        refreshTrigger={refreshTrigger}
      />

      <Dialog
        open={modalOpen}
        onClose={handleCloseModal}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Cavalos da raça: {selectedBreed?.horseBreedName}
        </DialogTitle>
        <DialogContent dividers>
          {loadingHorses ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <HorseLoader />
            </Box>
          ) : error ? (
            <Alert severity="error" sx={{ m: 2 }}>
              {error}
            </Alert>
          ) : horses.length > 0 ? (
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>ID</TableCell>
                    <TableCell>{t("fields.name")}</TableCell>
                    <TableCell>{t("fields.gender")}</TableCell>
                    <TableCell>Data Nasc.</TableCell>
                    <TableCell>Pai</TableCell>
                    <TableCell>Mãe</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {horses.map((horse: Horse) => (
                    <TableRow key={horse.horseId}>
                      <TableCell>{horse.horseId}</TableCell>
                      <TableCell>{horse.horseName}</TableCell>
                      <TableCell>{horse.horseGender}</TableCell>
                      <TableCell>{formatDate(horse.horseBirth)}</TableCell>
                      <TableCell>{horse.horseFather || '-'}</TableCell>
                      <TableCell>{horse.horseMother || '-'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Typography variant="body1" sx={{ p: 2 }}>
              Não foram encontrados cavalos relacionados a esta raça.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseModal} color="primary">
            Fechar
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}